/*eslint operator-assignment: ["error", "never"]*/

import Convert from './convert';
import { utils }  from './config';

/**
 * Class for Tow Item Selection calulations. This class contains 
 * normal filter, carbon filter calculations
 * @class Calculations
 * @param userPrefs User preferences object
 * @return Object
 * @requires userPreferences
 * @requires Convert
 */
function Calculations(userPrefs) {
    return {
        /**
         * This function will calculate the epd bias based on the 
         * rodepd, tipepd, rodlen, tiplen passed to the function 
         * and selected bias in the user preferences
         * @param {float} epd
         * @param {float} len
         * @requires userPrefs
         */
        EPDBiasCorrection: function (input) {
            var rodepd = parseFloat(input.rodepd) || 0;
            var tipepd = parseFloat(input.tipepd) || 0;
            var rodlen = parseFloat(input.rodlen) || 0;
            var tiplen = parseFloat(input.tiplen) || 0;
            var epdCarbon = parseFloat(input.epdCarbon) || 0;
            var epdBiasData = userPrefs.epdBiasData || {};

            // to avoid infinity situation, we are making
            // the default value as 1
            var epdiffSlope = epdBiasData.slope || 1;
            var epdIntercept = epdBiasData.intercept || 0;
            
            rodepd = (rodepd - epdIntercept * rodlen) / epdiffSlope;
            tipepd = (tipepd - epdIntercept * tiplen) / epdiffSlope;
            epdCarbon = (epdCarbon - epdIntercept * 1) / epdiffSlope;

            input.rodepd = rodepd;
            input.tipepd = tipepd;
            input.epdCarbon = epdCarbon;
        },

        /**
         * Returns the pressure drop power factor based on the
         * tip circumference value
         * @param {Float} tipcirc
         */
        pressureDropPowerFactor: function (input) {
            var tipcirc = parseFloat(input.tipcirc);

            if (isNaN(tipcirc)) {
                return 0;
            }

            if (tipcirc < 17.5) {
                return 7.15;
            } else if (tipcirc >= 17.5 && tipcirc < 20) {
                return 6.35;
            } else if (tipcirc >=20) {
                return 6.063;
            }

            input.tipcirc = tipcirc;
        },


        targetPD: function(input) {
            var minpd = parseFloat(input.minpd) || 0;
            var maxpd = parseFloat(input.maxpd) || 0;
            var minwt = parseFloat(input.minwt) || 0; 
            var maxwt = parseFloat(input.maxwt) || 0; 
            var towowt = parseFloat(input.towowt) || 0;

            input.tgtpd = (towowt - minwt) / (maxwt - minwt) * (maxpd - minpd) + minpd;
        },

        /**
         * Returns the slope and intercept for maxpd, minpd
         * minwt, maxwt values.
         * @param {Object} input
         */
        slopeAndIntercept: function (input) {
            var maxpd = parseFloat(input.maxpd) || 0;
            var minpd = parseFloat(input.minpd) || 0;
            var minwt = parseFloat(input.minwt) || 0;

            // to avoid infinity situation, we are making
            // the default value as 1
            var maxwt = parseFloat(input.maxwt) || 1;

            var slope = 0;
            var intercept = 0;

            // if maxwt is less than or equal
            // to minwt, slope will set to 1
            if (maxwt <= minwt) {
                slope = 1;
            } else {
                slope = (maxpd - minpd) / (maxwt - minwt);
            }
            
            intercept = minpd - slope * minwt;

            //assign the values to original passed object.
            input.slope = slope;
            input.intercept = intercept;
        },

        /**
         * Returns an adjusted minwt value, based on the
         * selected rod maker speed and plant code.
         * @param {object} input
         * @requires userPrefs
         */
        minPtAdjustmentByPlant: function (input) {
            var plantcode = input.plantcode;
            var minwt = input.minwt || 0;
            var userRodMakerSpeed;
            var finalAdjustment;
            var adjustments = {
                //1 - <100 m/m
                '1': {
                    'C': -1.0,
                    'S': -1.5,
                    'L': -1.5,
                    'O': -1.5
                },

                //2 - 100 m/m - 200 m/m
                '2': {
                    'C': -0.5,
                    'S': -1.0,
                    'L': -1.0,
                    'O': -1.0
                },

                //3 - 200 m/m - 500 m/m
                '3': {
                    'C': 0,
                    'S': 0,
                    'L': 0,
                    'O': 0
                }
            };

            userRodMakerSpeed = parseInt(userPrefs.rodMaker, 10);
            if (isNaN(userRodMakerSpeed)) {
                userRodMakerSpeed = 3;
            }

            minwt = parseFloat(minwt);
            if (isNaN(minwt)) {
                return 0;
            }

            // if plantcode is not string, assign C as the default
            // value. Also, try to convert the plantcode to uppercase
            plantcode = (typeof plantcode !== 'string') ?  'C' : plantcode.toUpperCase();

            // if we don't find the adjustment value because of wrong plantcode,
            // the adjustment will be defaulted to zero
            finalAdjustment = adjustments[userRodMakerSpeed][plantcode] || 0;

            //assign the values to original passed object.
            minwt = minwt + finalAdjustment;
            input.minwt = minwt;
            input.minpd = minwt * input.slope + input.intercept;
        },

        /**
         * Calculates tow yield to customer's filter dimensions
         * @param {Object} input
         */
        yieldPredictionModel: function (input) {
            var slope = input.slope || 0;
            var slope0 = input.slope || 0;
            var minwt = input.minwt || 0;
            var minpd = input.minpd || 0;
            var stdlen = input.stdlen || 1;
            var stdcirc = input.stdcirc || 0;
            var tipcirc = input.tipcirc || 0;
            var pwthick = input.pwthick;
            var stdthick = input.stdthick || 1;
            var ndpf = input.ndpf || 1;
            var lotatd = input.lotatd || 0;

            var iStdcirc, 
                iTipcirc,
                coefDifMinWt,
                coefDifMinPD,
                coefDifSlope,
                diffMinwt,
                diffMinpd,
                diffSlope; 

            //needed for regressions
            var td = lotatd / 1000;
            
            //Converts Min Point to 108 mm for model use
            minwt = minwt / stdlen * 108;
            minpd = minpd / stdlen * 108;

            iStdcirc = (stdcirc / Math.PI - 2 * stdthick) * Math.PI;
            iTipcirc = (tipcirc / Math.PI - 2 * pwthick) * Math.PI;

            //Coefficient definition for difference factors calculation
            // in ES6 you can use 2 ** 2 = 4, instead of Math.pow(2,2)
            if (stdcirc > 20) { 
                //Std Item
                coefDifMinWt = -0.009766 - 0.6618 * (1 / ndpf) + 0.000558 * td + 0.02278 * (td / ndpf) + 0.3472 * (1 / ndpf) ** 2 - 0.000079 * td ** 2 - 0.00046 * (td / ndpf) ** 2;
                coefDifMinPD = -0.2682 - 0.3478 * (1 / ndpf) + 0.000903 * td + 0.01667 * (td / ndpf) + 0.08919 * (1 / ndpf) ** 2 - 0.000078 * td ** 2 - 0.00039 * (td / ndpf) ** 2;
                coefDifSlope = -0.447 + 1.51 * (1 / ndpf) - 0.0041 * td - 0.0365 * (td / ndpf) - 1.35 * (1 / ndpf) ** 2 + 0.000124 * td ** 2 + 0.00103 * (td / ndpf) ** 2;
            } else {
                //Super Slims Item
                coefDifMinWt = -0.1045 + 0.004743 * ndpf;
                coefDifMinPD = -0.4646 + 0.01559 * ndpf;
                coefDifSlope = -0.6175 + 0.03453 * ndpf;
            }
            
            //Difference Factors Calculation
            diffMinwt = coefDifMinWt * (iTipcirc - iStdcirc);
            diffMinpd = coefDifMinPD * (iTipcirc - iStdcirc);
            diffSlope = coefDifSlope * (iTipcirc - iStdcirc);

            minwt = minwt * (iTipcirc / iStdcirc) ** 2 * Math.exp(diffMinwt);
            minpd = minpd * (iTipcirc / iStdcirc) ** 2 * Math.exp(diffMinpd);
            slope = slope * (iTipcirc / iStdcirc) ** 2 * Math.exp(diffSlope);

            //assign values to the original passed object
            input.minwt = minwt;
            input.minpd = minpd;
            input.slope = slope;
            input.slope0 = slope0;
        },

        /**
         * Calculates removal efficiencies with and without kretek
         * @param {Object} input
         */
        removalEfficienciesCalc(input) {
            let tiplen = input.tiplen || 0;
            let tipepd = input.tipepd || 0;
            let tipcirc = input.tipcirc || 0;
            let carbontip = input.carbontip || 0;
            let kretek = userPrefs.kretek;
            let ndpf = input.ndpf || 0;
            let sre, tre, nre, ere, ssre, stre, snre;

            sre = 100 * (1 - Math.exp(-0.01494 * tiplen - 0.000000013 * tipepd * tipcirc ** 4 - 0.01228 * tiplen / ndpf)) + 0.05 * carbontip;
            tre = 100 * (1 - Math.exp(-0.01068 * tiplen - 0.00000001134 * tipepd * tipcirc ** 4 - 0.01148 * tiplen / ndpf)) + 0.05 * carbontip;
            nre = 100 * (1 - Math.exp(-0.006743 * tiplen - 0.00000001075 * tipepd * tipcirc ** 4 - 0.01582 * tiplen / ndpf)) - 0.06 * carbontip;

            ssre = 100 * (1 - Math.exp(-0.0090815 * tiplen - 1.85217 * 0.00000001 * tipepd * tipcirc ** 4 - (0.019635 * tiplen / ndpf))) + 0.05 * carbontip;
            stre = 100 * (1 - Math.exp(-0.0062599 * tiplen - 2.11143 * 0.00000001 * tipepd * tipcirc ** 4 - (0.006425 * tiplen / ndpf))) + 0.05 * carbontip;
            snre = 100 * (1 - Math.exp(-0.0078493 * tiplen - 0.63051 * 0.00000001 * tipepd * tipcirc ** 4 - (0.034674 * tiplen / ndpf))) - 0.06 * carbontip;

            
            if (tipcirc > 19 && tipcirc <= 21) {
                sre = ((tipcirc - 19) / (21 - 19)) * sre + (1 - (tipcirc - 19) / (21 - 19)) * ssre
                tre = ((tipcirc - 19) / (21 - 19)) * tre + (1 - (tipcirc - 19) / (21 - 19)) * stre
                nre = ((tipcirc - 19) / (21 - 19)) * nre + (1 - (tipcirc - 19) / (21 - 19)) * snre
            } else if (tipcirc <= 19) {
                sre = ssre
                tre = stre
                nre = snre
            }

            //Kretek calculations

            if (kretek) {

                if (tipepd <= 54 || (ndpf >= 6 && input.newTowItem === true)) {
                    sre = (Math.exp(-0.00597 * tipepd - 0.00877 * tiplen - 0.0189 * tiplen / ndpf) - 1) * (-100);
                    tre = (Math.exp(-0.00541 * tipepd - 0.00332 * tiplen - 0.0285 * tiplen / ndpf) - 1) * (-100);
                    nre = (Math.exp(-0.00405 * tipepd - 0.00515 * tiplen + 0.00648 * tiplen / ndpf) - 1) * (-100);
                    ere = (Math.exp(-0.00636 * tipepd - 0.0229 * tiplen - 0.0003 * tiplen / ndpf) - 1) * (-100);
                } else {
                    sre = (Math.exp(-1.7638 * 0.00000001 * tipepd * ((tipcirc) ** 4) + (-0.01024) * tiplen - 0.00906 * tiplen / ndpf) - 1) * (-100);
                    tre = (Math.exp(-1.46 * 0.00000001 * tipepd * ((tipcirc) ** 4) + (-0.01036) * tiplen - 0.00606 * tiplen / ndpf) - 1) * (-100);
                    nre = (Math.exp(-1.899 * 0.00000001 * tipepd * ((tipcirc) ** 4) + 0.0139 * tiplen - 0.0553 * tiplen / ndpf) - 1) * (-100);
                    ere = (Math.exp(-9.307 * 0.000000001 * tipepd * ((tipcirc) ** 4) + (-0.02116) * tiplen + 0.01774 * tiplen / ndpf) - 1) * (-100);
                }
            }

            input.sre = sre;
            input.tre = tre;
            input.nre = nre;
            input.ere = ere;
        },


        /**
         * calculates the range extension 
         * @param {object} options
         */
        rangeExtension: function (input) {
            var userPrefRangeLen = input.usrRangeLenEnabled;
            var userPrefTransJet = userPrefs.transportJet;
            var usrange = input.usrange || 0;
            var tipcirc = input.tipcirc || 0;
            var rodlen = input.rodlen || 1;
            var slope = input.slope || 0;
            var minwt = input.minwt || 0;
            var minpd = input.minpd || 0;
            var maxwt = input.maxwt || 0;
            var maxpd = input.maxpd || 0;
            var rodepd = input.rodepd || 0;
            var rangeExtStd, rangeExtSS;

            minwt = minwt/108 * rodlen;
            minpd = minpd/108 * rodlen;

            //range length is not user specified
            if (!userPrefRangeLen) {
                //  range length is calculated
                switch (userPrefTransJet) {
                    case 3:
                        rangeExtStd = 12;
                        rangeExtSS = 8;
                        break;
                    case 2:
                        rangeExtStd = 9;
                        rangeExtSS = 6;
                        break;
                    case 1:
                        rangeExtStd = 7;
                        rangeExtSS = 4;
                        break;
                    default:
                        //do nothing
                }

                usrange = (tipcirc - 16.7) / (24.45 - 16.7) * (rangeExtStd - rangeExtSS) + rangeExtSS;
                usrange = usrange / 102 * rodlen;
            }

            
            maxwt = minwt + usrange;
            maxpd = slope * usrange + minpd;

            //assign the values to the original passed object
            input.minwt = minwt;
            input.maxwt = maxwt;
            input.minpd = minpd;
            input.maxpd = maxpd;
            input.tgtpd = rodepd;
            input.usrange = usrange;
        },

        /**
         * Adjust weights, pd, towowt, slope, lotpir based on PZ
         * @param {object} input
         */
        predictPDIncreaseByPZ: function (input) {
            var rodlen = input.rodlen || 0;
            var gphminwt = input.gphminwt || 0;
            var gphmaxwt = input.gphmaxwt || 0;
            var gphmaxpd = input.gphmaxpd || 0;
            var gphminpd = input.gphminpd || 0;
            var gphtgtpd;
            var minwt = input.minwt || 0;
            var maxwt = input.maxwt || 0;
            var minpd = input.minpd || 0;
            var maxpd = input.maxpd || 0;
            var tgtpd = input.tgtpd || 0;
            var pwthick = input.pwthick;
            var tipcirc = input.tipcirc || 0;
            var slope = input.slope || 0;
            var pz = input.pz || 0;
            var usrange = input.usrange || 0;
            

            var lotpir, towowt;

            var pz_pd_a = -378.284744559259;
            var pz_pd_b = 258704.397816947;
            var pz_pd_c = 466.497749813144;
            var pz_pd_d = 108;

            var pz_pd_pz;
            var pz_pd_tipcirc;
            var pz_pd_inc;
            var gphtgtwt;

            
            //Internal circumference
            pz_pd_tipcirc = (tipcirc / Math.PI - 2 * pwthick) * Math.PI;

            //TODO: check with Paco
            
            //App old code
            pz_pd_pz = pz / 100 * pz_pd_d / rodlen;

            //Paco's old code
            pz_pd_pz = gphminwt / 100 * 108 / rodlen * pz / 100




            //%PZ calculation for wet basis or dry basis
            //PZ @ min point, in g, 108mm
            pz_pd_pz = minwt / 100 * pz_pd_d / rodlen * pz/100;

            //MinWgt Adjust for PZ
            //pz_pd_inc = ((pz_pd_a * gphminwt / 100 * pz_pd_pz) + pz_pd_b * gphminwt / 100 * pz_pd_pz / pz_pd_tipcirc ** 2 + pz_pd_c * (gphminwt / 100 * pz_pd_pz) ** 2) * rodlen / pz_pd_d;            
            //pz_pd_min = gphminpd + pz_pd_inc;

            pz_pd_inc =  (pz_pd_a * pz_pd_pz + pz_pd_b * pz_pd_pz / pz_pd_tipcirc ** 2 + pz_pd_c * pz_pd_pz ** 2) * rodlen / 108
            gphminpd = minpd + pz_pd_inc;

            //PZ @ max point, in g, 108mm
            pz_pd_pz = maxwt / 100 * pz_pd_d / rodlen * pz/100;
            //MaxWt Adjust for PZ 

            //pz_pd_inc = ((pz_pd_a * gphmaxwt / 100 * pz_pd_pz) + pz_pd_b * gphmaxwt / 100 * pz_pd_pz / pz_pd_tipcirc ** 2 + pz_pd_c * (gphmaxwt / 100 * pz_pd_pz) ** 2) * rodlen / pz_pd_d;
            //gphmaxpd = gphmaxpd + pz_pd_inc;

            pz_pd_inc = (pz_pd_a * pz_pd_pz + pz_pd_b * pz_pd_pz / pz_pd_tipcirc ** 2 + pz_pd_c * pz_pd_pz ** 2) * rodlen / 108
            gphmaxpd = maxpd + pz_pd_inc

            gphminwt = minwt
            gphmaxwt = maxwt
            gphtgtpd = tgtpd

            slope = (gphmaxpd - gphminpd) / (gphmaxwt - gphminwt);
            
            //Extend MinWgt to ghpminpd
            // if (gphmaxwt !== gphminwt) {
            //     slope = (gphmaxpd - pz_pd_min) / (gphmaxwt - gphminwt);
            //     gphminwt = gphminwt - ((pz_pd_min - gphminpd) / slope);
            // }

            //Correct Range Extension
            gphmaxwt = gphminwt + usrange;
            gphmaxpd = slope * usrange + gphminpd;

            if (gphmaxpd === gphminpd) {
                lotpir = -1;
            } else {
                lotpir = (gphtgtpd - gphminpd) / (gphmaxpd - gphminpd) * 100; 
            }

            gphtgtwt = lotpir / 100 * (gphmaxwt - gphminwt) + gphminwt;
            towowt = gphtgtwt;

            input.towowt = towowt;
            input.gphtgtwt = gphtgtwt;
            input.gphminwt = gphminwt;
            input.gphmaxwt = gphmaxwt;
            input.gphminpd = gphminpd;
            input.gphmaxpd = gphmaxpd;
            input.gphtgtpd = gphtgtpd;
            input.slope = slope;
            input.lotpir = lotpir;
        },
        

        /*================================
            CARBON FUNCTIONS
        ==================================*/

        /**
         * Calculates carbon capability predictions
         * @param {Object} input
         */
        predictCarbonCapabilities: function (input) {
            var gphmaxpd = input.gphmaxpd || 0;
            var gphmaxwt = input.gphmaxwt || 0;
            var gphminpd = input.gphminpd || 0;
            var gphminwt = input.gphminwt || 0;
            var gphtgtpd = input.gphtgtpd || 0;
            var rodlen = input.rodlen || 1;
            var slope = input.slope || 0;
            
            var Cmodel_Tminwt,
                Cmodel_Tmaxwt,
                CModel_TowMinPD,
                CModel_TowMaxPD,
                Cmodel_tgtEPD,
                Cmodel_Tslope,
                Cmodel_Tint;
            
            // Convert gph variables to 108 mm
            Cmodel_Tminwt = gphminwt * 108 / rodlen;
            Cmodel_Tmaxwt = gphmaxwt * 108 / rodlen;
            CModel_TowMinPD = gphminpd * 108 / rodlen;
            CModel_TowMaxPD = gphmaxpd * 108 / rodlen;
        
            Cmodel_tgtEPD = (gphtgtpd * 108 / rodlen);

            if (gphminwt === gphmaxwt) {
                Cmodel_Tslope = 0;
            } else {
                Cmodel_Tslope = slope;
            }

            Cmodel_Tint = CModel_TowMinPD - Cmodel_Tslope * Cmodel_Tminwt; 

            //assign the calculated values
            input.Cmodel_Tminwt = Cmodel_Tminwt;
            input.Cmodel_Tmaxwt = Cmodel_Tmaxwt;
            input.CModel_TowMinPD = CModel_TowMinPD;
            input.CModel_TowMaxPD = CModel_TowMaxPD;
            input.Cmodel_tgtEPD = Cmodel_tgtEPD;
            input.Cmodel_Tslope = Cmodel_Tslope;
            input.Cmodel_Tint = Cmodel_Tint;

        },

         /**
         * Calculates carbon capability load
         * @param {Object} input
         */
        predictCarbonCapabilityLoad: function (input) {
            var tipcirc = input.tipcirc || 0;
            var cload = input.cload || 0;
            var rodlen = input.rodlen || 0;
            var Cmodel_Tminwt = input.Cmodel_Tminwt || 0;
            var Cmodel_Tmaxwt = input.Cmodel_Tmaxwt || 0;
            var CModel_TowMinPD = input.CModel_TowMinPD || 0;
            var CModel_TowMaxPD = input.CModel_TowMaxPD || 0;
            var Cmodel_tgtEPD = input.Cmodel_tgtEPD || 0;
            var Cmodel_Tslope = input.Cmodel_Tslope || 0;
            var Cmodel_Tint = input.Cmodel_Tint || 0;
            var CModelB1 = -0.02715997;
            var CModelB2 = 1.3359689;
            var CModelC1 = 0.007263044186;
            var CModelC2 = -0.8692886657;
            var CSlope = 1.25;

            var CModelA1,
                CModelA2,
                IntMin,
                IntMax,
                Cmodel_tgtC_minwt,
                Cmodel_tgtC_minEPD,
                Cmodel_tgtC_maxwt,
                Cmodel_tgtC_maxEPD,
                Cmodel_TowtattgtEPD,
                Cmodel_tgtWgt,
                C_TOEPD,
                lotpir,
                towowt,
                Cmodel_pir,                
                gphtgtwt,
                gphtgtpd,
                carbontip;

            CModelA1 = CModelB1 * tipcirc + CModelB2;
            CModelA2 = CModelC1 * tipcirc + CModelC2;

            IntMin = CModelA1 * Math.log(CModel_TowMinPD) + CModelA2;
            IntMax = CModelA1 * Math.log(CModel_TowMaxPD) + CModelA2;

            //Yield Line Tow+Carbon @ 108mm
            Cmodel_tgtC_minwt = Cmodel_Tminwt + 10.8 * cload;

            
            if (cload === 0) {
                Cmodel_tgtC_minEPD = CModel_TowMinPD;
            } else {
                Cmodel_tgtC_minEPD = CModel_TowMinPD + Math.exp(CSlope * Math.log(cload) + IntMin);
            }
            
            Cmodel_tgtC_maxwt = Cmodel_Tmaxwt + 10.8 * cload;

            if (cload === 0) {
                Cmodel_tgtC_maxEPD = CModel_TowMaxPD;
            } else {
                Cmodel_tgtC_maxEPD = CModel_TowMaxPD + Math.exp(CSlope * Math.log(cload) + IntMax);
            }

            Cmodel_tgtWgt = (Cmodel_tgtEPD - Cmodel_tgtC_minEPD) / (Cmodel_tgtC_maxEPD - Cmodel_tgtC_minEPD) * (Cmodel_tgtC_maxwt - Cmodel_tgtC_minwt) + Cmodel_tgtC_minwt;
            Cmodel_pir = (Cmodel_tgtEPD - Cmodel_tgtC_minEPD) / (Cmodel_tgtC_maxEPD - Cmodel_tgtC_minEPD) * 100;

            //Wgt Tow @ TGT CWgt @108
            gphtgtwt = Cmodel_tgtWgt - 10.8 * cload;
            //EPD Tow @ TGT Wgt @108
            gphtgtpd = gphtgtwt * Cmodel_Tslope + Cmodel_Tint;
            //PIR Tow Only
            lotpir = (gphtgtwt - Cmodel_Tminwt) / (Cmodel_Tmaxwt - Cmodel_Tminwt) * 100;

            //Adjusts Tow+Carbon Yield and Tow TGT to Customer rodlen
            Cmodel_tgtC_minwt = Cmodel_tgtC_minwt / 108 * rodlen;
            Cmodel_tgtC_minEPD = Cmodel_tgtC_minEPD / 108 * rodlen;
            Cmodel_tgtC_maxwt = Cmodel_tgtC_maxwt / 108 * rodlen;
            Cmodel_tgtC_maxEPD = Cmodel_tgtC_maxEPD / 108 * rodlen;
            Cmodel_tgtWgt = Cmodel_tgtWgt / 108 * rodlen;
            Cmodel_tgtEPD = Cmodel_tgtEPD / 108 * rodlen;
            carbontip = rodlen * cload;

            gphtgtwt = gphtgtwt / 108 * rodlen;
            towowt = gphtgtwt
            gphtgtpd = gphtgtpd / 108 * rodlen;

            input.Cmodel_tgtC_minwt= Cmodel_tgtC_minwt;
            input.Cmodel_tgtC_minEPD = Cmodel_tgtC_minEPD;
            input.Cmodel_tgtC_maxwt = Cmodel_tgtC_maxwt;
            input.Cmodel_tgtC_maxEPD = Cmodel_tgtC_maxEPD;
            input.Cmodel_tgtEPD = Cmodel_tgtEPD;
            input.Cmodel_tgtWgt = Cmodel_tgtWgt;
            input.Cmodel_pir = Cmodel_pir;
            input.Cmodel_TowtattgtEPD = Cmodel_TowtattgtEPD;
            input.gphtgtpd = gphtgtpd; 
            input.gphtgtwt = gphtgtwt;           
            input.towowt = towowt;
            input.C_TOEPD = C_TOEPD;
            input.lotpir = lotpir;
            input.carbontip = carbontip;
            input.IntMin = IntMin;
            input.IntMax = IntMax;
        },

        /**
         * Calculates carbon capability load Manual
         * @param {Object} input
         */
        predictCarbonCapabilityLoadManual: function (input) {
            var gphmaxpd = input.gphmaxpd || 0;
            var gphmaxwt = input.gphmaxwt || 0;
            var gphminpd = input.gphminpd || 0;
            var gphminwt = input.gphminwt || 0;
            var epdCarbon = input.epdCarbon || 0;
            var Cmodel_tgtEPD = input.Cmodel_tgtEPD || 0;
            var rodlen = input.rodlen || 1;
            var slope = input.slope || 0;
            var cload = input.cload || 0;

            var lotpir, towowt, gphtgtwt, gphtgtpd, Cmodel_tgtWgt, Cmodel_tgtC_minwt, 
                Cmodel_tgtC_maxwt, Cmodel_tgtC_minEPD, Cmodel_tgtC_maxEPD, Cmodel_pir;

            gphtgtpd = Cmodel_tgtEPD - epdCarbon * rodlen;
            gphtgtwt = (gphtgtpd - gphminpd) / (gphmaxpd - gphminpd) * (gphmaxwt - gphminwt) + gphminwt;
            lotpir = (gphtgtwt - gphminwt) / (gphmaxwt - gphminwt) * 100;
            towowt = gphtgtwt;

            Cmodel_tgtWgt = gphtgtwt + cload * rodlen / 10;
            Cmodel_tgtC_minwt = gphminwt + cload * rodlen / 10;
            Cmodel_tgtC_maxwt = gphmaxwt + cload * rodlen / 10;

            Cmodel_tgtC_minEPD = Cmodel_tgtEPD - slope * (Cmodel_tgtWgt - Cmodel_tgtC_minwt);
            Cmodel_tgtC_maxEPD = Cmodel_tgtEPD - slope * (Cmodel_tgtWgt - Cmodel_tgtC_maxwt);

            Cmodel_pir = (Cmodel_tgtEPD - Cmodel_tgtC_minEPD) / (Cmodel_tgtC_maxEPD - Cmodel_tgtC_minEPD) * 100;

            input.gphtgtpd = gphtgtpd;
            input.gphtgtwt = gphtgtwt;
            input.lotpir = lotpir;
            input.towowt = towowt;
            input.Cmodel_tgtWgt = Cmodel_tgtWgt;
            input.Cmodel_tgtC_minwt = Cmodel_tgtC_minwt;
            input.Cmodel_tgtC_minEPD = Cmodel_tgtC_minEPD;
            input.Cmodel_tgtC_maxEPD = Cmodel_tgtC_maxEPD;
            input.Cmodel_pir = Cmodel_pir;
        },

        /**
         * Calculates max carbon loading
         * @param {Object} input
         */
        predictMaxCarbonLoading: function (input) {
            var userCarbRangeLen = input.carbRangeLenEnabled;
            var uscrange = input.uscrange || 0;
            var cload = input.cload || 0;
            var IntMax = input.IntMax || 0;
            var IntMin = input.IntMin || 0;
            var CMax_const = 11.58329;
            var CMax_m = -1.88667;
            var CMax_Min,
                CMax_Max,
                crange,
                cpir,
                lotpir = input.lotpir;


            if (userCarbRangeLen) {
                CMax_const = uscrange;
                CMax_m = 0;
            }

            CMax_Min = CMax_const + CMax_m * IntMin;
            CMax_Max = CMax_const + CMax_m * IntMax;
            crange = (CMax_Max - CMax_Min) * lotpir / 100 + CMax_Min;

            cpir = cload / crange * 100;

            input.CMax_Min = CMax_Min;
            input.CMax_Max = CMax_Max;
            input.crange = crange;
            input.cpir = cpir;
        },

        /**
         * Calculates Adjusted Yield to customer dimensions
         * @param {Object} input
         */
        adjustYieldtoCustDimensions: function (input) {

            var Cmodel_tgtC_minwt = input.Cmodel_tgtC_minwt || 0;
            var Cmodel_tgtC_minEPD = input.Cmodel_tgtC_minEPD || 0;
            var Cmodel_tgtC_maxwt = input.Cmodel_tgtC_maxwt || 0;
            var Cmodel_tgtC_maxEPD = input.Cmodel_tgtC_maxEPD || 0;
            var Cmodel_tgtWgt = input.Cmodel_tgtWgt || 0;
            var Cmodel_tgtEPD;
            var Cmodel_TowtattgtEPD = input.Cmodel_TowtattgtEPD || 0;            
            var rodlen = input.rodlen || 1;
            var gphtgtpd = input.gphtgtpd || 0;
            var gphtgtwt;
            var towowt;
            var C_TOEPD = input.C_TOEPD || 0;

            //Carbon+Tow yield data to Tow Only and user dimensions
            Cmodel_tgtC_minwt = Cmodel_tgtC_minwt / 108 * rodlen;
            Cmodel_tgtC_minEPD = Cmodel_tgtC_minEPD / 108 * rodlen;
            Cmodel_tgtC_maxwt = Cmodel_tgtC_maxwt / 108 * rodlen;
            Cmodel_tgtC_maxEPD = Cmodel_tgtC_maxEPD / 108 * rodlen;
            Cmodel_tgtWgt = Cmodel_tgtWgt / 108 * rodlen;
            Cmodel_tgtEPD = gphtgtpd;

            //Tow Only yield data
            //gphmin and gphmax data (wgt and pd) remain untouched
            Cmodel_TowtattgtEPD = Cmodel_TowtattgtEPD / 108 * rodlen;
            gphtgtwt = Cmodel_TowtattgtEPD;
            towowt = gphtgtwt;

            C_TOEPD = C_TOEPD / 108 * rodlen;
            gphtgtpd = C_TOEPD;

            input.Cmodel_tgtC_minwt = Cmodel_tgtC_minwt;
            input.Cmodel_tgtC_minEPD = Cmodel_tgtC_minEPD;
            input.Cmodel_tgtC_maxwt = Cmodel_tgtC_maxwt;
            input.Cmodel_tgtC_maxEPD = Cmodel_tgtC_maxEPD;
            input.Cmodel_tgtWgt = Cmodel_tgtWgt;
            input.Cmodel_tgtEPD = Cmodel_tgtEPD;      
            input.Cmodel_TowtattgtEPD = Cmodel_TowtattgtEPD;
            input.gphtgtpd = gphtgtpd;
            input.gphtgtwt = gphtgtwt;
            input.towowt = towowt;

        },

        /**
         * Calculates firmness
         * @param {Object} input
         */
        firmness: function (input) {
            var towowt = input.towowt || 0;
            var tipcirc = input.tipcirc || 0;
            var pwthick = input.pwthick;
            var rodlen = input.rodlen || 0;
            var ndpf = input.ndpf || 0;
            var pz = input.pz || 0;
            var td = input.lotatd || 0;
            var tipdiam; 

            var towowt_grod,
                towcirc,
                vol,
                drydepress,
                delf_intercept,
                delf_maxinc,
                delf_timespread,
                f0,
                f_a,
                f_b,
                f_c,
                f_d,
                maxd_red,
                pz_at_maxf,
                maxf,
                mind_at_11pz,
                maxf_at_11pz,
                corr_maxf,
                corr_finc,
                f_adjfactor,
                pz_dep_red_cure,
                dpn;

            tipdiam = Convert.toDiameter(tipcirc);
            towowt_grod = Convert.fromG100toG(towowt);

            towcirc = (tipcirc / Math.PI - 2 * (pwthick)) * Math.PI;
            vol = (towcirc * towcirc) / (4 * Math.PI) * rodlen;

            drydepress = 1.439627 - 1543.68913362699 * (towowt_grod / rodlen) - 1845.0666236934 * (towowt_grod / vol)
            + 0.0109998045426391 * (towcirc ** 2) + 0.000728787756677038 * (td / ndpf) - 0.0335141087602546 * ((td / ndpf) ** 2) * 0.000001 
            + 0.512788980699212 * ((towowt_grod * 0.000001 * td ** 2) / rodlen) + 1.05884183145802 * ((towowt_grod * ndpf * 1000000) / (td * rodlen))
            - 0.0000771100478926286 * (towcirc * 0.000001 * (td ** 2)) - 11.5883158195033 * (towcirc / (0.000001 * td ** 2)) - 0.0000206952317431375 *
            ((towcirc * td) / ndpf) + 0.00104743307241026 * ((towcirc * 0.000001 * td ** 2) / (ndpf ** 2));

            f0 = (1 - drydepress / tipdiam) * 100;

            delf_intercept = 3.80812460381885 - 0.411156506202073 * (ndpf) + 0.0725766849312726 * (f0) + 1.00517956647328 * (towcirc) - 0.347615688760182 * (td / 1000);

            delf_maxinc = 0.350210885196977 - 0.0452035431836739 * (ndpf) + 0.124744181880588 * (drydepress) + 0.0158910340354362 * (towcirc) - 0.00395319813813105 * (td / 1000);

            delf_timespread = -0.0300521181050448 + 0.00332074035458127 * (ndpf) + 0.0608914440982973 * (drydepress) + 0.00418948702503068 * (towcirc) - 0.00178274148397475 * (td / 1000);


            f_a = -4 * delf_timespread / (delf_intercept * delf_intercept);
            f_b = -4 * delf_maxinc / (delf_intercept * delf_intercept);
            f_c = -f_a * delf_intercept;
            f_d = -f_b * delf_intercept;
            maxd_red = -((f_c * Math.log(24) + f_d) ** 2) / (4 * (f_a * Math.log(24) + f_b));
            pz_at_maxf = delf_intercept / 2;
            maxf = (1 - drydepress - maxd_red / tipdiam) * 100;
            mind_at_11pz = (f_a * Math.log(24) + f_b) * 121 + (f_c * Math.log(24) + f_d) * 11;
            maxf_at_11pz = (1 - drydepress - mind_at_11pz / tipdiam) * 100;

            if (maxf < 94) {
                corr_maxf = maxf;
            } else if (pz_at_maxf < 11) {
                corr_maxf = Math.sqrt(maxf - 93) + 93;
            } else if (maxf_at_11pz < 94) {
                corr_maxf = maxf;
            } else {
                corr_maxf = Math.sqrt(maxf_at_11pz - 93) + 93;
            }

            corr_finc = corr_maxf - f0;
            if (pz_at_maxf > 11) {
                f_adjfactor = corr_finc / (maxf_at_11pz - f0);
            } else {
                f_adjfactor = corr_finc / (maxf - f0);
            }

            pz_dep_red_cure = ((f_a * Math.log(24) + f_b) * (pz ** 2) + (f_c * Math.log(24) + f_d) * pz) * f_adjfactor;
            dpn = drydepress - pz_dep_red_cure;

            input.dpn = dpn;
            input.f_a = f_a;
            input.f_b = f_b;
            input.f_c = f_c;
            input.f_d = f_d;
            input.f_adjfactor = f_adjfactor;
            input.drydepress = drydepress;

        },

         /**
         * Calculates firmness cured
         * @param {Object} input
         */
        firmnessCured: function (input) {

            var dpn = input.dpn;
            var userFMethod = userPrefs.firmnessMethod;
            var tipcirc = input.tipcirc || 0;
            var tipdiam = Convert.toDiameter(tipcirc);
            var dpnadj;
            var firmness;

            dpn = (-0.0685 + dpn) / 1.436;
            switch (userFMethod) {
                case 'Filtrona QTM': //Filtrona QTM
                    dpnadj = 1.436 * dpn + 0.0685;
                    break;
                case 'Borgwaldt': //Borgwaldt
                    dpnadj = 0.9728 * dpn + 0.6089;
                    break;
                case 'Borgwaldt w/1 kg wt': //Borgwaldt w/1 kg wt
                    dpnadj = 1.3514 * dpn + 0.6284;
                    break;
                default: //all the rest of Firnmess methods
                    dpnadj = dpn;
            }

            firmness = 100 - (dpnadj / tipdiam) * 100;

            input.firmness = firmness;
        },

        /**
         * Calculates firmness intermediate
         * @param {Object} input
         */
        firmnessIntermediate: function (input, time) {

            time = isNaN(time) ? 2 : parseFloat(time);

            var tipcirc = input.tipcirc || 0;
            var tipdiam = Convert.toDiameter(tipcirc);
            let f_a= input.f_a;
            let f_b= input.f_b;
            let f_c= input.f_c;
            let f_d= input.f_d;
            let f_adjfactor = input.f_adjfactor;
            let pz = input.pz;
            let pz_dep_red_int = ((f_a * Math.log(time) + f_b) * (pz ** 2) + (f_c * Math.log(time) + f_d) * pz) * f_adjfactor;

            if (time === 0) {
                pz_dep_red_int = 0;
            }
            
            let dpn = input.drydepress - pz_dep_red_int;
            let userFMethod = userPrefs.firmnessMethod;
            let dpnadj, firmnessi;
            
            dpn = (-0.0685 + dpn) / 1.436

            switch (userFMethod) {
                case 'Filtrona QTM': //Filtrona QTM
                    dpnadj = 1.436 * dpn + 0.0685;
                    break;
                case 'Borgwaldt': //Borgwaldt
                    dpnadj = 0.9728 * dpn + 0.6089;
                    break;
                case 'Borgwaldt w/1 kg wt': //Borgwaldt w/1 kg wt
                    dpnadj = 1.3514 * dpn + 0.6284;
                    break;
                default: //all the rest of Firnmess methods
                    dpnadj = dpn;
            }

            firmnessi = 100 - dpnadj / tipdiam * 100;
            input.firmnessIntermediate = firmnessi; 

        },

        /**
         * Adjusts tow only weight to wet weight
         * @param {Object} input
         */
        adjustWeightToWetWeight: function (input) {
            var userPrefRangeLen = input.usrRangeLenEnabled;
            var gphminwt = input.gphminwt || 0;
            var gphmaxwt = input.gphmaxwt || 0;
            var gphminpd = input.gphminpd || 0;
            var gphtgtpd = input.gphtgtpd || 0;
            var minwt = input.minwt || 0;
            var maxwt = input.maxwt || 0;
            var gphmaxpd = input.gphmaxpd || 0;
            var gphtgtwt = input.gphtgtwt || 0;
            var Cmodel_tgtC_minEPD = input.Cmodel_tgtC_minEPD || 0;
            var Cmodel_tgtC_maxEPD;
            var Cmodel_tgtEPD  = input.Cmodel_tgtEPD || 0;
            var rodpgwt = input.rodpgwt || 0;
            var towowt = input.towowt || 0;
            var usrange = input.usrange || 0;
            var pzwt;
            var pz = input.pz || 0;
            var Cmodel_tgtC_minwt = input.Cmodel_tgtC_minwt || 0;
            var Cmodel_tgtC_maxwt = input.Cmodel_tgtC_maxwt || 0;
            var Cmodel_tgtWgt = input.Cmodel_tgtWgt || 0;
            var cload = input.cload || 0;

            //add PW weight to filter  (Dry Filter)
            gphminwt = gphminwt + rodpgwt;
            gphmaxwt = gphmaxwt + rodpgwt;
            gphtgtwt = gphtgtwt + rodpgwt;

            //Calculates wet filter
            gphminwt = gphminwt * (1 + pz / 100);
            gphmaxwt = gphmaxwt * (1 + pz / 100);
            gphtgtwt = gphtgtwt * (1 + pz / 100);

            if (userPrefRangeLen) {
                gphmaxwt = gphminwt + usrange;
                gphmaxpd = (gphmaxwt - gphminwt) / (gphtgtwt - gphminwt) * (gphtgtpd - gphminpd) + gphminpd;
            }


            pzwt = gphtgtwt - (rodpgwt + towowt);

            //Wet filter carbon
            if (cload !== 0) {
                Cmodel_tgtC_minwt = Cmodel_tgtC_minwt + (gphminwt - minwt);
                Cmodel_tgtC_maxwt = Cmodel_tgtC_maxwt + (gphmaxwt - maxwt);
                Cmodel_tgtWgt = Cmodel_tgtWgt + (gphtgtwt - towowt);
                Cmodel_tgtC_maxEPD = (Cmodel_tgtC_maxwt - Cmodel_tgtC_minwt) / (Cmodel_tgtWgt - Cmodel_tgtC_minwt) * (Cmodel_tgtEPD - Cmodel_tgtC_minEPD) + Cmodel_tgtC_minEPD;
            }

            input.gphminwt = gphminwt;
            input.gphmaxwt = gphmaxwt;
            input.gphtgtwt = gphtgtwt;
            input.gphmaxpd = gphmaxpd;
            input.pzwt = pzwt;
            input.Cmodel_tgtC_minwt = Cmodel_tgtC_minwt;
            input.Cmodel_tgtC_maxwt = Cmodel_tgtC_maxwt;
            input.Cmodel_tgtWgt = Cmodel_tgtWgt;
            input.Cmodel_tgtC_maxEPD = Cmodel_tgtC_maxEPD;
        },

        /**
         * Adjusts Dep Pressure Drop
         * @param {Object} input
         */
        AdjDepPresDrop: function (input) {

            var gphminpd = input.gphminpd || 0;
            var gphmaxpd = input.gphmaxpd || 0;
            var gphtgtpd = input.gphtgtpd || 0;
            var tgtpd = input.tgtpd || 0;
            var Cmodel_tgtC_minEPD = input.Cmodel_tgtC_minEPD || 0;
            var Cmodel_tgtC_maxEPD = input.Cmodel_tgtC_maxEPD || 0;
            var Cmodel_tgtEPD = input.Cmodel_tgtEPD || 0;
            var tipepd = input.tipepd || 0;
            var rodpgwt = input.rodpgwt || 0;
            var pzwt = input.pzwt || 0;

            var gphminwt = input.gphminwt || 0;
            var gphmaxwt = input.gphmaxwt || 0;
            var gphtgtwt = input.gphtgtwt || 0;
            var Cmodel_tgtC_minwt = input.Cmodel_tgtC_minwt || 0;
            var Cmodel_tgtC_maxwt = input.Cmodel_tgtC_maxwt || 0;
            var Cmodel_tgtWgt = input.Cmodel_tgtWgt || 0;

            var towowt = input.towowt || 0;

            if (userPrefs.lowpdlim === 2) {
                gphminpd = Convert.toPascals(gphminpd);
                gphmaxpd = Convert.toPascals(gphmaxpd);
                gphtgtpd = Convert.toPascals(gphtgtpd);
                tgtpd = Convert.toPascals(tgtpd);
                Cmodel_tgtC_minEPD = Convert.toPascals(Cmodel_tgtC_minEPD);
                Cmodel_tgtC_maxEPD = Convert.toPascals(Cmodel_tgtC_maxEPD);
                Cmodel_tgtEPD = Convert.toPascals(Cmodel_tgtEPD);
                tipepd = Convert.toPascals(tipepd);
            }

            switch (userPrefs.weight) {
                case 3: // g
                    gphminwt = gphminwt / 100;
                    gphmaxwt = gphmaxwt / 100;
                    gphtgtwt = gphtgtwt / 100;

                    Cmodel_tgtC_minwt = Cmodel_tgtC_minwt / 100;
                    Cmodel_tgtC_maxwt = Cmodel_tgtC_maxwt / 100;
                    Cmodel_tgtWgt = Cmodel_tgtWgt / 100;

                    rodpgwt = rodpgwt / 100;
                    pzwt = pzwt / 100;

                    towowt = towowt / 100;
                    break;
                case 2: // mg
                    gphminwt = gphminwt * 10;
                    gphmaxwt = gphmaxwt * 10;
                    gphtgtwt = gphtgtwt * 10;

                    Cmodel_tgtC_minwt = Cmodel_tgtC_minwt * 10;
                    Cmodel_tgtC_maxwt = Cmodel_tgtC_maxwt * 10;
                    Cmodel_tgtWgt = Cmodel_tgtWgt * 10;

                    rodpgwt = rodpgwt * 10;
                    pzwt = pzwt * 10;

                    towowt = towowt * 10;
                    break;
                default:
                    // do nothing
            }

            input.range_ext = gphmaxwt - gphminwt;
            input.Cmodel_range_ext = Cmodel_tgtC_maxwt - Cmodel_tgtC_minwt;
            input.gphminpd = gphminpd;
            input.gphmaxpd = gphmaxpd;
            input.gphtgtpd = gphtgtpd;
            input.tgtpd = tgtpd;
            input.Cmodel_tgtC_minEPD = Cmodel_tgtC_minEPD;
            input.Cmodel_tgtC_maxEPD = Cmodel_tgtC_maxEPD;
            input.Cmodel_tgtEPD = Cmodel_tgtEPD;
            input.gphminwt = gphminwt;
            input.gphmaxwt = gphmaxwt;
            input.gphtgtwt = gphtgtwt;
            input.Cmodel_tgtC_minwt = Cmodel_tgtC_minwt;
            input.Cmodel_tgtC_maxwt = Cmodel_tgtC_maxwt;
            input.Cmodel_tgtWgt = Cmodel_tgtWgt;
            input.rodpgwt = rodpgwt;
            input.pzwt = pzwt;
            input.tipepd = tipepd;
            input.towowt = towowt;
        },

        /**
         * Reverts EPD Bias corrections
         * @param {Object} input
         */
        EPDBiasCorrectionsRevert: function (input) {
            var gphminpd = input.gphminpd || 0;
            var gphmaxpd = input.gphmaxpd || 0;
            var gphtgtpd = input.gphtgtpd || 0;
            var Cmodel_tgtC_minEPD = input.Cmodel_tgtC_minEPD || 0;
            var Cmodel_tgtC_maxEPD = input.Cmodel_tgtC_maxEPD || 0;
            var Cmodel_tgtEPD = input.Cmodel_tgtEPD || 0;
            var tipepd = input.tipepd || 0;
            var rodlen = input.rodlen || 0;
            var tiplen = input.tiplen || 0;

            var epdBiasData = userPrefs.epdBiasData || {};
            var epdSlope = epdBiasData.slope;
            var epdIntercept = epdBiasData.intercept;

            if (typeof epdSlope !== 'undefined') {
                gphminpd = epdSlope * gphminpd + epdIntercept * rodlen;
                gphmaxpd = epdSlope * gphmaxpd + epdIntercept * rodlen;
                gphtgtpd = epdSlope * gphtgtpd + epdIntercept * rodlen;

                Cmodel_tgtC_minEPD = epdSlope * Cmodel_tgtC_minEPD + epdIntercept * rodlen;
                Cmodel_tgtC_maxEPD = epdSlope * Cmodel_tgtC_maxEPD + epdIntercept * rodlen;
                Cmodel_tgtEPD = epdSlope * Cmodel_tgtEPD + epdIntercept * rodlen;

                tipepd = epdSlope * tipepd + epdIntercept * tiplen;
            }

            input.gphminpd = gphminpd;
            input.gphmaxpd = gphmaxpd;
            input.gphtgtpd = gphtgtpd;
            input.Cmodel_tgtC_minEPD = Cmodel_tgtC_minEPD;
            input.Cmodel_tgtC_maxEPD = Cmodel_tgtC_maxEPD;
            input.Cmodel_tgtEPD = Cmodel_tgtEPD;
            input.tipepd = tipepd;
        },

        /**
         * Returns a new tow item based on the inputs provided
         * @param {Object} input
         */
        newTowItem: function (input) {
            var td = input.td || 0;
            var ndpf = input.dpf || 0;

            td = td / 1000;

            var intercept,
                slope,
                minwt,
                minpd,
                stdcirc = 24.45,
                stdlen = 102,
                stdthick = 0.042,
                lot = utils.getTranslationText('lbl_predicted', 'Predicted'),
                item = '*'+ ndpf.toFixed(1) + '/' + td,
                itemlot = ndpf + '/' + td  + utils.getTranslationText('lbl_lot_predicted', 'Lot Predicted');

            minwt = 12.028 + 0.8814 * ndpf + 0.92584 * td + 0.18202 * td / ndpf;
            minpd = -34.33 + 1.849 * ndpf + 2.3165 * td + 17.0463 * td / ndpf;
            slope = 2.679 - 0.08031 * ndpf + 0.01019 * td + 0.79424 * td / ndpf;

            if (td < 23) {  //Slims Equations    
                minwt = 10.209 - 0.23369 * ndpf + 1.1323 * td - 1.1441 * td / ndpf;
                minpd = 73.2 - 6.06 * ndpf + 6.66 * td + 66.8 * td / ndpf;
                slope = -15.64 - 0.1711 * ndpf + 2.3949 * td + 3.95 * td / ndpf;
                stdcirc = 16.7;
            }

            intercept = minpd - slope * minwt;
            td = td * 1000;

            return {
                td: td,
                lotatd: td, 
                ndpf: ndpf,
                dpf: ndpf,
                isNew: true,
                lotadpf: ndpf,
                slope: slope,
                intercept: intercept,
                stdcirc: stdcirc,
                stdthick: stdthick,
                lot: lot,
                item: item,
                itemlot: itemlot,
                minwt: minwt,
                minpd: minpd,
                stdlen: stdlen,
                status: true
            };

        },

        /**
         * Removal efficiencies calculations for delivery
         * @param {Object} input
         */
        removalEfficienciesCalcDelivery: function (input) {

            var avlsmk = input.avlsmk;
            var avltar = input.avltar;
            var avlnic = input.avlnic;
            var avleug = input.avleug;
            var sre = input.sre;
            var tre = input.tre;
            var nre = input.nre;
            var ere = input.ere;
            var tipvent = input.tipvent;
            var tipcirc = input.tipcirc;
            var smkdelo, tardelo, codelo = input.codelo, nicdelo, eugdelo;
            var smkfract, tarfract, cofract, nicfract, keugfract;
            var smkdelx, tardelx, codelx, nicdelx, eugdelx;

            smkdelo = avlsmk * (1 - sre / 100);
            tardelo = avltar * (1 - tre / 100);
            nicdelo = avlnic * (1 - nre / 100);
            eugdelo = avleug * (1 - ere / 100);

            if (tipvent === 0) {
                smkdelx = smkdelo;
                tardelx = tardelo;
                nicdelx = nicdelo;
                eugdelx = eugdelo;
                codelx = codelo;
            } else {
                if (tipcirc >= 20.5) {
                    smkfract = 1.0 - 0.00952 * tipvent;
                    tarfract = 1.0068 - 0.00945 * tipvent;
                    nicfract = 0.988 - 0.000114 * tipvent ** 2;
                    cofract = 1.0021 - 0.0122 * tipvent;
                } else if (tipcirc > 19.5) {
                    smkfract = (tipcirc - 19.5) * (1.0 - 0.00952 * tipvent) + (20.5 - tipcirc) * (1 - 0.01 * tipvent);
                    tarfract = (tipcirc - 19.5) * (1.0068 - 0.00945 * tipvent) + (20.5 - tipcirc) * (1 - 0.01 * tipvent);
                    nicfract = (tipcirc - 19.5) * (0.988 - 0.000114 * tipvent ** 2) + (20.5 - tipcirc) * (1 - 0.0001 * tipvent ** 2);
                    cofract = (tipcirc - 19.5) * (1.0021 - 0.0122 * tipvent) + (20.5 - tipcirc) * (1 - 0.01 * tipvent);
                } else if (tipcirc <= 19.5) {
                    smkfract = 1 - 0.01 * tipvent;
                    tarfract = 1 - 0.01 * tipvent;
                    nicfract = 1 - 0.0001 * tipvent ** 2;
                    cofract = 1 - 0.01 * tipvent;
                }

                keugfract = 1.0 - 0.00623 * tipvent  //No new regression for Eugenol Superslims

                smkfract = smkfract > 1.0 ? 1.0 : smkfract;
                tarfract = tarfract > 1.0 ? 1.0 : tarfract;
                nicfract = nicfract > 1.0 ? 1.0 : nicfract;
                cofract = cofract > 1.0 ? 1.0 : cofract;
                keugfract = keugfract > 1.0 ? 1.0 : keugfract;

                smkdelx = smkdelo * smkfract;
                tardelx = tardelo * tarfract;
                nicdelx = nicdelo * nicfract;
                codelx = codelo * cofract;
                eugdelx = eugdelo * keugfract;
            }

            return {
                smkdelx,
                tardelx,
                nicdelx,
                codelx,
                eugdelx,
                smkdelo,
                tardelo,
                nicdelo,
                codelo,
                eugdelo
            };

        },

        /**
         * Hot collapse calculations
         * @param {Object} input
         */
        hotCollapse: function(input) {
            var towowt = input.towowt;
            var tipsrod  = input.tipsrod;
            var pz = input.pz;
            var ndpf = input.ndpf;
            var avlsmk = input.avlsmk;
            var sre = input.sre;
            var tipvent = input.tipvent;

            var smkdelo, tiptowowt, ventLoad, ventLoado,tipLoad, hotC;

            smkdelo = avlsmk * (1 - sre / 100);
            ventLoado = avlsmk - smkdelo;
            ventLoad = (1-0.00822*tipvent) * ventLoado;

            tiptowowt = towowt/tipsrod * 10;
            tipLoad = ventLoad/tiptowowt * 100;
            
            hotC = 44.1 + 0.471 * pz - 2.91 * ndpf - 0.399 * tipvent - 0.858 * tipLoad - 0.0165 * pz * tipvent + 0.00867 * tipvent * tipLoad;

            return {
                tipLoad,
                hotC
            }
        },

        /**
         * Availables when Tipvent is zero
         * @param {Object} input
         */
        availablesWhenTipVentZero: function (input) {
            var smkdelx = input.smkdelx;
            var tardelx = input.tardelx; 
            var nicdelx = input.nicdelx; 
            var codelx = input.codelx;
            var eugdelx = input.eugdelx || 0;
            var tipvent = input.tipvent;
            var sre = input.sre;
            var tre = input.tre;
            var nre = input.nre;
            var ere = input.ere || 0;
            var avlsmk, avltar, avlnic, avleug, codelo;

            if (tipvent === 0) {
                avlsmk = smkdelx / (1 - sre / 100);
                avltar = tardelx / (1 - tre / 100);
                avlnic = nicdelx / (1 - nre / 100);
                codelo = codelx;
                avleug = eugdelx / (1 - ere / 100);
            }

            return {
                avlsmk: avlsmk || 0,
                avltar: avltar || 0,
                avlnic: avlnic || 0,
                avlco: codelo || 0,
                avleug: avleug || 0
            };
        },

        /**
         * Availables when Tipvent is not zero
         * @param {Object} input
         */
        availablesWhenTipVentNotZero:function (input) {
            
            var smkdelx = input.smkdelx;
            var tardelx = input.tardelx; 
            var nicdelx = input.nicdelx; 
            var codelx = input.codelx;
            var eugdelx = input.eugdelx || 0;
            var tipvent = input.tipvent;
            var tipcirc = input.tipcirc;
            var sre = input.sre;
            var tre = input.tre;
            var nre = input.nre;
            var ere = input.ere || 0;
            var avlsmk, avltar, avlnic, avleug;
            var smkfract, tarfract, nicfract, cofract, keugfract, codelo;

            if (tipcirc >= 20.5) {
                smkfract = 1.0 - 0.00952 * tipvent;
                tarfract = 1.0068 - 0.00945 * tipvent;
                nicfract = 0.988 - 0.000114 * tipvent ** 2;
                cofract = 1.0021 - 0.0122 * tipvent;
            } else if (tipcirc > 19.5) {
                smkfract = (tipcirc - 19.5) * (1.0 - 0.00952 * tipvent) + (20.5 - tipcirc) * (1 - 0.01 * tipvent);
                tarfract = (tipcirc - 19.5) * (1.0068 - 0.00945 * tipvent) + (20.5 - tipcirc) * (1 - 0.01 * tipvent);
                nicfract = (tipcirc - 19.5) * (0.988 - 0.000114 * tipvent ** 2) + (20.5 - tipcirc) * (1 - 0.0001 * tipvent ** 2);
                cofract = (tipcirc - 19.5) * (1.0021 - 0.0122 * tipvent) + (20.5 - tipcirc) * (1 - 0.01 * tipvent);
            } else if (tipcirc <= 19.5) {
                smkfract = 1 - 0.01 * tipvent;
                tarfract = 1 - 0.01 * tipvent;
                nicfract = 1 - 0.0001 * tipvent ** 2;
                cofract = 1 - 0.01 * tipvent;
            }

            keugfract = 1 - 0.00623 * tipvent;
            smkfract = smkfract > 1.0 ? 1.0 : smkfract;
            tarfract = tarfract > 1.0 ? 1.0 : tarfract;
            nicfract = nicfract > 1.0 ? 1.0 : nicfract;
            cofract = cofract  > 1.0 ? 1.0 : cofract;
            keugfract = keugfract > 1.0 ? 1.0 : keugfract;

            avlsmk = (smkdelx / smkfract) / (1 - (sre / 100));
            avltar = (tardelx / tarfract) / (1 - (tre / 100));
            avlnic = (nicdelx / nicfract) / (1 - (nre / 100));
            codelo = (codelx / cofract);
            avleug = (eugdelx / keugfract) / (1 - ere / 100);
            

            return {
                avlsmk: avlsmk || 0,
                avltar: avltar || 0,
                avlnic: avlnic || 0,
                avlco: codelo || 0,
                avleug: avleug || 0
            };
        }
    };
        
}
export default Calculations;


// WEBPACK FOOTER //
// ./src/calculations.js